import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, RotateCcw, Loader2, CheckCircle2, AlertTriangle, TrendingUp, Info, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "baseline", label: "Company baseline" },
  { key: "cash", label: "Cash cycle" },
  { key: "supply", label: "Supply & sales" },
  { key: "mfg", label: "Manufacturing" },
  { key: "constraint", label: "Constraint" },
];

const emptyForm = {
  company: "",
  industry: "",
  annual_revenue_inr_cr: "",
  dso_days: "",
  inventory_days: "",
  dpo_days: "",
  otif_pct: "",
  oee_pct: "",
  scrap_pct: "",
  top_customer_concentration_pct: "",
  top_supplier_concentration_pct: "",
  primary_bottleneck: "",
  contact_email: "",
};

export default function DiagnosticPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const canSubmit = form.dso_days !== "" && form.inventory_days !== "" && form.dpo_days !== "" &&
    form.otif_pct !== "" && form.oee_pct !== "" && form.scrap_pct !== "" &&
    form.top_customer_concentration_pct !== "" && form.top_supplier_concentration_pct !== "" && form.primary_bottleneck !== "";

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please fill all metric fields before running the diagnostic.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        annual_revenue_inr_cr: form.annual_revenue_inr_cr === "" ? null : Number(form.annual_revenue_inr_cr),
        dso_days: Number(form.dso_days),
        inventory_days: Number(form.inventory_days),
        dpo_days: Number(form.dpo_days),
        otif_pct: Number(form.otif_pct),
        oee_pct: Number(form.oee_pct),
        scrap_pct: Number(form.scrap_pct),
        top_customer_concentration_pct: Number(form.top_customer_concentration_pct),
        top_supplier_concentration_pct: Number(form.top_supplier_concentration_pct),
        contact_email: form.contact_email || null,
      };
      const res = await axios.post(`${API}/diagnostic`, payload);
      setResult(res.data);
      toast.success("Diagnostic complete.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setForm(emptyForm); setStep(0); setResult(null); };

  if (result) return <ResultView result={result} onReset={reset} />;

  return (
    <div data-testid="diagnostic-page" className="mx-auto max-w-[1200px] px-6 lg:px-10 py-16">
      <div className="max-w-3xl">
        <span className="eyebrow">Constraint Diagnostic</span>
        <h1 className="mt-4 text-4xl lg:text-5xl tracking-[-0.03em] font-semibold text-slate-900 leading-[1.05]">
          Five minutes to a quantified constraint map.
        </h1>
        <p className="mt-5 text-[17px] text-slate-600 leading-[1.7]">
          Enter your operational baseline. The engine isolates the primary system constraint, quantifies the cash-flow gap
          versus benchmark, and returns a prioritised 30-90 day + 6-12 month plan — with trade-offs disclosed.
        </p>
      </div>

      {/* Progress */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <ol className="space-y-2">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                data-testid={`step-${i}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  i === step ? "bg-slate-900 text-white" : i < step ? "text-slate-900" : "text-slate-400"
                }`}
              >
                <span className={`font-mono-tabular text-xs w-6 text-center rounded-md py-0.5 ${
                  i === step ? "bg-white/15 text-white" : i < step ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                }`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-medium">{s.label}</span>
              </li>
            ))}
          </ol>
          <div className="mt-6">
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
            <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-slate-500 font-mono-tabular">
              Step {step + 1} / {STEPS.length}
            </div>
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="ind-card p-6 lg:p-8">
            {step === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Company (optional)" testid="f-company">
                  <Input value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="e.g. Acme Metals Ltd" data-testid="input-company" />
                </Field>
                <Field label="Industry (optional)" testid="f-industry">
                  <Input value={form.industry} onChange={(e) => update("industry", e.target.value)} placeholder="Metals · Rail · Energy · Process" data-testid="input-industry" />
                </Field>
                <Field label="Annual revenue (₹ Cr, optional)" testid="f-rev" hint="Used to size the cash impact. Defaults to ₹100 Cr if left blank.">
                  <Input type="number" value={form.annual_revenue_inr_cr} onChange={(e) => update("annual_revenue_inr_cr", e.target.value)} placeholder="e.g. 450" data-testid="input-revenue" />
                </Field>
                <Field label="Your email (optional)" testid="f-email" hint="We'll email a copy of the diagnostic to you and the Indusynq team.">
                  <Input type="email" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} placeholder="you@company.com" data-testid="input-email" />
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Field label="DSO (days)" testid="f-dso" hint="Days Sales Outstanding — how quickly customers pay.">
                  <Input type="number" value={form.dso_days} onChange={(e) => update("dso_days", e.target.value)} placeholder="e.g. 62" data-testid="input-dso" />
                </Field>
                <Field label="Inventory Days" testid="f-inv" hint="Total DOH across raw / WIP / finished.">
                  <Input type="number" value={form.inventory_days} onChange={(e) => update("inventory_days", e.target.value)} placeholder="e.g. 78" data-testid="input-inv" />
                </Field>
                <Field label="DPO (days)" testid="f-dpo" hint="Days Payable Outstanding — how quickly you pay suppliers.">
                  <Input type="number" value={form.dpo_days} onChange={(e) => update("dpo_days", e.target.value)} placeholder="e.g. 34" data-testid="input-dpo" />
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="OTIF %" testid="f-otif" hint="On-Time In-Full delivery rate over the last 90 days.">
                  <Input type="number" value={form.otif_pct} onChange={(e) => update("otif_pct", e.target.value)} placeholder="e.g. 72" data-testid="input-otif" />
                </Field>
                <Field label="Top-3 customer concentration (%)" testid="f-cust" hint="% of total revenue from top-3 customers combined.">
                  <Input type="number" value={form.top_customer_concentration_pct} onChange={(e) => update("top_customer_concentration_pct", e.target.value)} placeholder="e.g. 48" data-testid="input-cust" />
                </Field>
                <Field label="Top supplier concentration (%)" testid="f-sup" hint="% of critical spend concentrated in a single supplier.">
                  <Input type="number" value={form.top_supplier_concentration_pct} onChange={(e) => update("top_supplier_concentration_pct", e.target.value)} placeholder="e.g. 65" data-testid="input-sup" />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Bottleneck OEE (%)" testid="f-oee" hint="OEE at the identified constraint asset, not plant-average.">
                  <Input type="number" value={form.oee_pct} onChange={(e) => update("oee_pct", e.target.value)} placeholder="e.g. 58" data-testid="input-oee" />
                </Field>
                <Field label="Scrap / Yield loss (%)" testid="f-scrap" hint="First-pass scrap at or downstream of the constraint.">
                  <Input type="number" value={form.scrap_pct} onChange={(e) => update("scrap_pct", e.target.value)} placeholder="e.g. 4.5" data-testid="input-scrap" />
                </Field>
              </div>
            )}

            {step === 4 && (
              <div className="max-w-xl">
                <Field label="Primary system bottleneck" testid="f-btl" hint="Choose the category best describing what governs total throughput today.">
                  <Select value={form.primary_bottleneck} onValueChange={(v) => update("primary_bottleneck", v)}>
                    <SelectTrigger data-testid="select-bottleneck"><SelectValue placeholder="Select constraint category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="machine">Machine / Physical asset</SelectItem>
                      <SelectItem value="process">Process step (setup, changeover)</SelectItem>
                      <SelectItem value="skill">Skill / Manpower</SelectItem>
                      <SelectItem value="policy">Policy (batch, MOQ, credit terms)</SelectItem>
                      <SelectItem value="supplier">Supplier / Upstream input</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
              <button
                onClick={prev}
                disabled={step === 0}
                data-testid="btn-prev"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-sm font-medium px-4 py-2 text-slate-700 disabled:opacity-40 hover:bg-slate-50"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  onClick={next}
                  data-testid="btn-next"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-medium px-5 py-2.5 hover:bg-black"
                >
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={loading || !canSubmit}
                  data-testid="btn-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[#712ae2] text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-60 hover:bg-[#5f21c2]"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                  Run diagnostic
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, testid, children }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">{label}</Label>
      <div className="mt-2">{children}</div>
      {hint && <div className="mt-1.5 text-[12px] text-slate-500">{hint}</div>}
    </div>
  );
}

function ResultView({ result, onReset }) {
  const domainLabel = { FCF: "Free Cash Flow", Sales: "Sales & Margin", SupplyChain: "Supply Chain", Manufacturing: "Manufacturing" };
  const domainDot = { FCF: "bg-emerald-500", Sales: "bg-violet-500", SupplyChain: "bg-amber-500", Manufacturing: "bg-sky-500" };
  const sevStyle = {
    critical: "bg-red-50 border-red-200 text-red-900",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
    info: "bg-slate-50 border-slate-200 text-slate-900",
  };
  const sevIcon = { critical: AlertTriangle, warn: AlertTriangle, info: Info };
  const quick = result.recommendations.filter((r) => r.horizon === "quick_win");
  const structural = result.recommendations.filter((r) => r.horizon === "structural");

  return (
    <div data-testid="diagnostic-result" className="mx-auto max-w-[1200px] px-6 lg:px-10 py-16 print-report">
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <div>
          <span className="eyebrow">Diagnostic Result</span>
          <h1 className="mt-3 text-3xl lg:text-5xl font-semibold tracking-[-0.03em] text-slate-900">Your constraint map.</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            data-testid="btn-download-pdf"
            className="inline-flex items-center gap-2 rounded-full bg-[#712ae2] text-white text-sm font-semibold px-4 py-2 hover:bg-[#5f21c2]"
          >
            <Download className="h-4 w-4" /> Download PDF
          </button>
          <button onClick={onReset} data-testid="btn-reset" className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-sm font-medium px-4 py-2 text-slate-700 hover:bg-slate-50">
            <RotateCcw className="h-4 w-4" /> Run again
          </button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="print-only mb-8">
        <div className="flex items-center gap-2">
          <span className="inline-block h-5 w-5 rounded bg-[#0F172A]" />
          <span className="text-xl font-semibold">Indusynq · Constraint Diagnostic</span>
        </div>
        <div className="text-xs text-slate-500 mt-1">Generated {new Date(result.created_at).toLocaleString()} · Report ID {result.id.slice(0, 8)}</div>
        <div className="hairline mt-4" />
      </div>

      {/* Headline */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl bg-[#0F172A] text-white p-8">
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-400">Cash Conversion Cycle</div>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="number-hero text-6xl">{result.ccc_days.toFixed(0)}</span>
            <span className="text-slate-400">days · benchmark {result.ccc_benchmark.toFixed(0)}</span>
          </div>
          <div className="mt-6 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#712ae2]" style={{ width: `${Math.min(100, (result.ccc_days / (result.ccc_benchmark * 2)) * 100)}%` }} />
          </div>
          <p className="mt-6 text-slate-300 leading-relaxed text-[15px]">{result.constraint_summary}</p>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-8 flex flex-col justify-between">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">Working Capital Release</div>
            <div className="mt-2 number-hero text-4xl text-slate-900">
              ₹{result.fcf_release_range_inr_cr[0]}<span className="text-slate-400">–</span>{result.fcf_release_range_inr_cr[1]}
              <span className="text-slate-500 text-lg font-normal"> Cr</span>
            </div>
            <div className="mt-2 text-[13px] text-emerald-600 font-medium">30–55% of CCC gap · 90-day horizon</div>
          </div>
          <div className="mt-6 border-t border-slate-100 pt-4">
            <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">Primary Constraint</div>
            <div className="mt-1 text-lg font-semibold text-slate-900 capitalize">{result.primary_constraint}</div>
          </div>
        </div>
      </div>

      {/* Domain scores */}
      <section className="mt-10">
        <span className="eyebrow">Domain Health</span>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          {Object.entries(result.domain_scores).map(([k, v]) => (
            <div key={k} data-testid={`score-${k}`} className="ind-card p-5">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${domainDot[k]}`} />
                <span className="text-sm text-slate-500">{domainLabel[k]}</span>
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="number-hero text-3xl text-slate-900">{v}</span>
                <span className="text-sm text-slate-400">/100</span>
              </div>
              <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${v >= 70 ? "bg-emerald-500" : v >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${v}%` }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Recommendations */}
      <section className="mt-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <RecommendationBlock title="Quick Wins" subtitle="30–90 days · low or zero CAPEX" items={quick} sevStyle={sevStyle} sevIcon={sevIcon} domainDot={domainDot} />
          <RecommendationBlock title="Structural Changes" subtitle="6–12 months · capital or organisational shift" items={structural} sevStyle={sevStyle} sevIcon={sevIcon} domainDot={domainDot} />
        </div>
      </section>

      <section className="mt-14 rounded-2xl bg-[#0F172A] text-white p-8 lg:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 text-[#A78BFA] text-[11px] tracking-[0.22em] uppercase"><CheckCircle2 className="h-4 w-4" /> This is a directional diagnostic</div>
          <h3 className="mt-3 text-2xl lg:text-3xl font-semibold tracking-tight">Want the boardroom-ready version?</h3>
          <p className="mt-2 text-slate-300 max-w-xl">A 2-week Diagnostic Sprint plants a validated constraint map, refined financial impact, and a signed-off plan.</p>
        </div>
        <a href="/contact" data-testid="result-contact-cta" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 text-sm font-semibold px-6 py-3">
          Book Diagnostic Sprint
        </a>
      </section>
    </div>
  );
}

function RecommendationBlock({ title, subtitle, items, sevStyle, sevIcon, domainDot }) {
  const domainLabel = { FCF: "FCF", Sales: "Sales", SupplyChain: "Supply", Manufacturing: "Mfg" };
  return (
    <div>
      <div>
        <span className="eyebrow">{title}</span>
        <h3 className="mt-2 text-2xl lg:text-3xl font-semibold tracking-tight text-slate-900">{subtitle}</h3>
      </div>
      <div className="mt-6 space-y-4">
        {items.length === 0 && <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-slate-500 text-sm">No items in this horizon — strong baseline.</div>}
        {items.map((r, i) => {
          const SevIcon = sevIcon[r.severity] || Info;
          return (
            <div key={i} data-testid={`rec-${r.horizon}-${i}`} className="ind-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`h-2 w-2 rounded-full ${domainDot[r.domain]}`} />
                    <span className="text-[11px] tracking-[0.22em] uppercase text-slate-500">{domainLabel[r.domain]}</span>
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">{r.title}</h4>
                </div>
                <SevIcon className={`h-5 w-5 shrink-0 ${r.severity === "critical" ? "text-red-500" : r.severity === "warn" ? "text-amber-500" : "text-slate-400"}`} />
              </div>
              <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[13.5px] text-emerald-900 font-medium inline-block">
                Impact: {r.financial_impact}
              </div>
              <p className="mt-3 text-[15px] text-slate-700 leading-relaxed">{r.action}</p>
              <div className={`mt-3 rounded-md border px-3 py-2 text-[13px] ${sevStyle[r.severity]}`}>
                <span className="font-semibold">Trade-off: </span>{r.tradeoff}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
